import { Languages } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Logo } from "@/components/common/Logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { languages } from "@/i18n";

type AuthShellProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
};

/** Layout of the public pages: sign-in, invitation, password reset, first run. */
export function AuthShell({ title, subtitle, children, footer, wide }: AuthShellProps) {
  const { t, i18n } = useTranslation();
  const nextLang = languages.find((l) => l !== i18n.resolvedLanguage) ?? "ru";

  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <header className="flex h-16 items-center justify-between px-6 md:px-8">
        <Logo />
        <Button variant="ghost" size="sm" onClick={() => void i18n.changeLanguage(nextLang)}>
          <Languages />
          {t(`languages.${nextLang}`)}
        </Button>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 pb-16">
        <div className={cn("flex w-full flex-col gap-6", wide ? "max-w-[560px]" : "max-w-[400px]")}>
          <div className="text-center">
            <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
            {subtitle && <p className="mt-1.5 text-muted">{subtitle}</p>}
          </div>
          <Card className="p-7">{children}</Card>
          {footer && <div className="text-center text-muted">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

/** Reads a one-time token from the URL fragment (#msi_... / #msp_...). */
// eslint-disable-next-line react-refresh/only-export-components
export function tokenFromHash(hash: string): string {
  return decodeURIComponent(hash.replace(/^#/, "")).trim();
}
